import clsx from 'clsx';

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { checkIfTimeIsKnown } from '@/utils/checkIfTimeIsKnown';
import { getTimezoneFromCountry } from '@/utils/getTimezoneFromCountry';
import { readFragment, type FragmentOf, type ResultOf } from '@/utils/graphql';
import { matchdayCardDateFragment } from './matchdayCardDateFragment';

export function MatchdayCardDate({
  matchday: _matchday,
}: {
  matchday: FragmentOf<typeof matchdayCardDateFragment>;
}) {
  const matchday = readFragment(matchdayCardDateFragment, _matchday);

  if (!matchday.start_datetime) {
    return null;
  }

  const { localDatetime, day, month } = getMatchdayLocalDatetime(matchday);

  return (
    <TooltipProvider delayDuration={0}>
      <Tooltip>
        <TooltipTrigger
          className={clsx(
            'flex flex-col items-center',
            matchday.postponed && 'line-through',
          )}
        >
          <div className="text-xl">{day}</div>
          <div className="text-xs uppercase">{month}</div>
        </TooltipTrigger>
        <TooltipContent className="first-letter:uppercase">
          {matchday.postponed ? 'Aplazado' : localDatetime}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function getMatchdayLocalDatetime(
  matchday: ResultOf<typeof matchdayCardDateFragment>,
) {
  const date = new Date(matchday.start_datetime!);
  const isTimeKnown = checkIfTimeIsKnown(matchday.start_datetime!);
  const timeZone = isTimeKnown
    ? matchday.location?.timezone ||
      getTimezoneFromCountry(matchday.country?.translations?.[0]?.name) ||
      'UTC'
    : 'UTC';

  const localDatetime = date.toLocaleString('es-ES', {
    timeZone,
    dateStyle: 'long',
  });
  const day = date.toLocaleDateString('es-ES', {
    timeZone,
    day: '2-digit',
  });
  const month = date.toLocaleDateString('es-ES', {
    timeZone,
    month: 'short',
  });

  return { localDatetime, day, month };
}
